import type { BottomApplicationLink } from "Components/Layout/Menu/BottomMenuItem";
import {
  type BottomApplicationLinkWithAccessCode,
  useBottomMenuLinks,
} from "Hooks/Layout/useBottomMenuLinks";
import { AccessRoleCodes, type AccessRoleCode } from "Models/AccessRoleCodes";
import type { RootStateType } from "State/Store";
import { useSelector } from "react-redux";

type UseBottomMenuReturn = Array<BottomApplicationLink>;

const filterByAccessRightCode = (
  links: Array<BottomApplicationLinkWithAccessCode>,
  allowedAccessRightCodes: Array<string>,
) =>
  links
    .filter(
      ({ accessRightCode }) =>
        !accessRightCode ||
        allowedAccessRightCodes.some((code) => code === accessRightCode),
    )
    .map(({ accessRightCode, ...rest }) => rest); //remove accessRightCode prop from the links

export const useBottomMenu = (
  userType: AccessRoleCode,
): UseBottomMenuReturn => {
  const currentUserAccessRightCodes =
    useSelector((e: RootStateType) => e.auth).user?.accessRights?.map(
      ({ code }) => code,
    ) || [];

  const {
    overview,
    settings,
    transactions,
    users,
    merchantKeypadPayment,
    amlUsers,
    stardust,
    valor,
    merchants,
    merchantSettlements,
    employeeKeypadPayment,
    thanksToYou,
    createMerchantRegistration,
    notificationsManagement,
    toBeVerified,
    hunters,
    whereToPay,
    // chat TODO: will be placed when menu items per role are decided
  } = useBottomMenuLinks();

  const USER_TYPE_TO_MENU: Record<
    AccessRoleCode,
    Array<BottomApplicationLink>
  > = {
    [AccessRoleCodes.Admin]: filterByAccessRightCode(
      [overview, users, toBeVerified, notificationsManagement, settings],
      currentUserAccessRightCodes,
    ),
    [AccessRoleCodes.AdminLite]: filterByAccessRightCode(
      [overview, users, toBeVerified, notificationsManagement, settings],
      currentUserAccessRightCodes,
    ),
    [AccessRoleCodes.Hunter]: filterByAccessRightCode(
      [overview, thanksToYou, createMerchantRegistration, valor, settings],
      currentUserAccessRightCodes,
    ),
    [AccessRoleCodes.HunterPlus]: filterByAccessRightCode(
      [overview, thanksToYou, createMerchantRegistration, valor, settings],
      currentUserAccessRightCodes,
    ),
    [AccessRoleCodes.Merchant]: filterByAccessRightCode(
      [
        overview,
        transactions,
        merchantKeypadPayment,
        merchantSettlements,
        settings,
      ],
      currentUserAccessRightCodes,
    ),
    [AccessRoleCodes.Customer]: filterByAccessRightCode(
      [overview, whereToPay, stardust, thanksToYou, settings],
      currentUserAccessRightCodes,
    ),
    [AccessRoleCodes.Aml]: filterByAccessRightCode(
      [overview, amlUsers, merchants, settings],
      currentUserAccessRightCodes,
    ),
    [AccessRoleCodes.Employee]: filterByAccessRightCode(
      [overview, employeeKeypadPayment, transactions, settings],
      currentUserAccessRightCodes,
    ),
    [AccessRoleCodes.CustomerOnboarding]: filterByAccessRightCode(
      [overview, users, merchants, hunters, toBeVerified, settings],
      currentUserAccessRightCodes,
    ),
    [AccessRoleCodes.Disponent]: [],
    [AccessRoleCodes.Child]: [],
  };

  return USER_TYPE_TO_MENU[userType];
};
